/**
 * Canonical RBAC definitions for Nova Asistencia / Nova ERP.
 *
 * Production data historically uses "user" for an employee account.
 * Legacy "employee" is normalized only at compatibility boundaries.
 */
export const ROLES = {
  SUPERADMIN: 'superadmin',
  ADMIN: 'admin',
  SUPERVISOR: 'supervisor',
  USER: 'user',
  EMPLOYEE: 'user',
} as const;

export type CanonicalRole =
  | typeof ROLES.SUPERADMIN
  | typeof ROLES.ADMIN
  | typeof ROLES.SUPERVISOR
  | typeof ROLES.USER;

export function normalizeRole(role?: string | null): CanonicalRole | '' {
  if (!role) return '';
  const normalized = role.trim().toLowerCase();

  if (normalized === 'employee') return ROLES.USER;

  if (
    normalized === ROLES.USER ||
    normalized === ROLES.SUPERVISOR ||
    normalized === ROLES.ADMIN ||
    normalized === ROLES.SUPERADMIN
  ) {
    return normalized as CanonicalRole;
  }

  return '';
}

export const ROLE_HIERARCHY: Record<CanonicalRole, number> = {
  [ROLES.SUPERADMIN]: 4,
  [ROLES.ADMIN]: 3,
  [ROLES.SUPERVISOR]: 2,
  [ROLES.USER]: 1,
};

export const ROLE_PERMISSIONS: Record<CanonicalRole, string[]> = {
  [ROLES.SUPERADMIN]: [
    'manage_users',
    'manage_employees',
    'manage_sites',
    'manage_attendance',
    'manage_payroll',
    'manage_incidents',
    'manage_reports',
    'manage_system',
    'manage_branding',
    'view_analytics',
  ],
  [ROLES.ADMIN]: [
    'manage_employees',
    'manage_sites',
    'manage_attendance',
    'manage_payroll',
    'manage_incidents',
    'manage_reports',
    'view_analytics',
    'manage_roles',
    'view_all_attendance',
  ],
  [ROLES.SUPERVISOR]: [
    'view_employees',
    'manage_attendance',
    'create_incidents',
    'view_reports',
  ],
  [ROLES.USER]: [
    'view_own_data',
    'clock_in_out',
    'view_own_payroll',
    'create_incidents',
  ],
};

export function hasRole(userRole: string, requiredRole: string): boolean {
  const normalizedUserRole = normalizeRole(userRole);
  const normalizedRequiredRole = normalizeRole(requiredRole);
  if (!normalizedUserRole || !normalizedRequiredRole) return false;

  return (
    ROLE_HIERARCHY[normalizedUserRole] >= ROLE_HIERARCHY[normalizedRequiredRole]
  );
}

export function getRolePermissions(role: string): string[] {
  const normalizedRole = normalizeRole(role);
  if (!normalizedRole) return [];

  // Permission sets are explicit by role. Role hierarchy is used by hasRole()
  // for route/access level checks, but must not implicitly grant lower-role
  // capabilities that were not present in the production RBAC contract.
  return [...ROLE_PERMISSIONS[normalizedRole]];
}

export function hasPermission(userRole: string, permission: string): boolean {
  if (!permission) return false;
  return getRolePermissions(userRole).includes(permission);
}
